import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CacheService } from './cache.service';

export interface LogAnalyticsWorkspace {
  id: string;
  name: string;
  location: string;
}

export interface QueryResult {
  tables: {
    columns: { name: string; type: string }[];
    rows: any[];
  }[];
}

@Injectable({
  providedIn: 'root',
})
export class LogAnalyticsService {
  private readonly baseUrl = 'https://management.azure.com';
  private readonly laUrl = 'https://api.loganalytics.io/v1';
  private readonly apiVersion = environment.logAnalyticsConfig.apiVersion;
  private readonly cachePrefix = 'loganalytics';
  private readonly cacheDuration = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  constructor(
    private readonly http: HttpClient,
    private readonly cacheService: CacheService
  ) {}

  getWorkspaces(): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/subscriptions/${environment.logAnalyticsConfig.subscriptionId}/providers/` +
        `Microsoft.OperationalInsights/workspaces?api-version=${this.apiVersion}`
    );
  }

  executeQuery(
    workspaceId: string,
    query: string,
    timeSpan: string = 'P1D',
    useClientCache: boolean = false,
    serverCacheAgeInSeconds: number = 60
  ): Observable<QueryResult> {
    const body = {
      query: query.replace('\n', ''),
    };
    const url = `${this.laUrl.toLowerCase()}${workspaceId.toLowerCase()}/query?&timespan=${timeSpan}`;
    const cacheKey = this.cacheService.generateCacheKey(this.cachePrefix, [workspaceId, query, timeSpan]);
    const options = {
      headers: {
        'Cache-Control': `max-age=${serverCacheAgeInSeconds.toString()}`
      }
    }
    return this.cacheService.getCachedOrFetch<QueryResult>(
      cacheKey,
      this.cacheDuration,
      () => this.http.post<QueryResult>(url, body, options),
      useClientCache
    );
  }

  queryAlertsByTeam(): Observable<QueryResult> {
    return this.executeQuery(
      environment.logAnalyticsConfig.workspaceId,
      environment.logAnalyticsConfig.alertsByTeamQuery,
      'P30D',
      true,
      600
    );
  }

  queryAlertsBreakdownByTeam(): Observable<QueryResult> {
    return this.executeQuery(
      environment.logAnalyticsConfig.workspaceId,
      environment.logAnalyticsConfig.alertsBreakdownByTeamQuery,
      'P30D',
      true,
      600
    );
  }
}
